import type { FC } from 'react';

import { Placeholder, SlotAddTiles, useRendering, useSetting } from '@eneco/cms';
import { Grid } from '@eneco/sparky';
import type { GridProps } from '@eneco/sparky/types';

import { LINE_UP_SLOTS, resolveSlot } from './slots';

type ResponsiveColumns = GridProps['columns'];

/** `Grid`'s `columns` only accepts `'1'`-`'12'` (`ColumnValue`, `Grid.tsx`) — clamp before stringifying. */
const columnCount = (count: number, max: 2 | 3): '1' | '2' | '3' => (count >= max ? `${max}` : `${Math.max(count, 1)}`) as '1' | '2' | '3';

/**
 * `LineUp` block for the CMS-agnostic lane, serving **both** providers.
 *
 * NOTE (schema gap): `items` is an unresolved reference stub with no usable label
 * (`line-up-content.types.ts`, since removed — nothing in `content` is renderable), so the legacy
 * component's bare-`items`-array fallback (`ArticleCard`/`ProductCard`, both Sitecore-bridge components
 * outside this registry regardless) is dropped; this component only renders its nested placeholders.
 * `jss-content-card-sm-compact-line-up-main` and `jss-testimonials-card-line-up-main` have no Sanity-side
 * field at all (only `jss-line-up-main`/`lineUpMain` and `jss-usp-line-up-main`/`uspLineUpMain` are
 * modeled), so those two always resolve empty there.
 *
 * Known simplification: the legacy component injects `mobileVariant: 'compact'` into individual USP /
 * content-card children by mutating their already-rendered JSX (`Object.assign(child.props...)`) — the
 * CMS-agnostic `<Placeholder>` renders opaquely and exposes no such mutation hook, so that per-child
 * override is dropped; `mobileVariant` still applies at the `LineUp` level via the column-count logic
 * below, matching the legacy default.
 */
const LineUp: FC = () => {
  const rendering = useRendering();

  const hasFixedWidth = useSetting('hasFixedWidthOption') === 'true';

  const mainSlot = resolveSlot(rendering, LINE_UP_SLOTS.main);
  const uspSlot = resolveSlot(rendering, LINE_UP_SLOTS.usp);
  const contentCardSmCompactSlot = resolveSlot(rendering, LINE_UP_SLOTS.contentCardSmCompact);
  const testimonialsCardSlot = resolveSlot(rendering, LINE_UP_SLOTS.testimonialsCard);

  const activeSlot = mainSlot ?? uspSlot ?? contentCardSmCompactSlot ?? testimonialsCardSlot;
  const itemCount = (activeSlot && rendering?.placeholders?.[activeSlot]?.length) || 1;

  const defaultColumns: ResponsiveColumns = {
    initial: '1',
    md: hasFixedWidth ? '2' : columnCount(itemCount, 2),
    xl: hasFixedWidth ? '3' : columnCount(itemCount, 3),
  };
  const columns: ResponsiveColumns = uspSlot && activeSlot === uspSlot ? { initial: '1', md: '2', lg: '4' } : defaultColumns;
  // Add tiles only for the slots it renders, so a Line Up of cards offers no USP items.
  const renderedSlots = [mainSlot, uspSlot, contentCardSmCompactSlot, testimonialsCardSlot].filter((slot): slot is string => slot !== undefined);
  const addSlots = renderedSlots.length > 0 ? renderedSlots : LINE_UP_SLOTS.main;

  return (
    // The add tiles sit after `Grid`, not in it, so they never take a grid cell.
    <>
      <Grid columns={columns} justifyContent="stretch" gridTemplateRows="1fr" gap={{ initial: '10', md: '16' }}>
        {mainSlot ? <Placeholder name={mainSlot} /> : null}
        {uspSlot ? <Placeholder name={uspSlot} /> : null}
        {contentCardSmCompactSlot ? <Placeholder name={contentCardSmCompactSlot} /> : null}
        {testimonialsCardSlot ? <Placeholder name={testimonialsCardSlot} /> : null}
      </Grid>
      <SlotAddTiles slots={addSlots} />
    </>
  );
};

export default LineUp;
